// Shared "search in progress" affordance for the recap/transport pickers.
//
// Two pieces, used together by `EntitySelectWithTags` and `ProductDropdown`:
// - `SearchPendingOverlay`: a translucent veil over the (stale) result rows.
//   It communicates "this list is not the answer yet" AND physically blocks
//   row clicks, so a mis-click during a slow search can't select a stale row.
// - `useSettleHeightAnimation`: freezes the list container's height while the
//   search is pending (the veil must not resize under the cursor) and animates
//   it to the new natural height once the settled results land.
import { useLayoutEffect, useRef, type RefObject } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface SearchPendingOverlayProps {
  /** True while the rendered results are stale vs. the live search term. */
  pending: boolean;
  /** Localized "Searching…" label. */
  label: string;
  testId?: string;
}

/**
 * Always mounted so the veil can fade out; when idle it is invisible and
 * click-through. Place inside a `relative` wrapper around the scrollable list.
 */
export function SearchPendingOverlay({
  pending,
  label,
  testId,
}: Readonly<SearchPendingOverlayProps>) {
  return (
    <div
      data-testid={testId}
      data-state={pending ? "active" : "idle"}
      aria-hidden={!pending}
      className={cn(
        "absolute inset-0 z-10 flex items-center justify-center",
        "bg-white/60 backdrop-blur-[1.5px] transition-opacity duration-150",
        pending ? "opacity-100" : "pointer-events-none opacity-0",
      )}
    >
      <div className="flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
        <span>{label}</span>
      </div>
    </div>
  );
}

const SETTLE_TRANSITION_MS = 180;

/**
 * Pins `ref`'s height while `pending` is true, then animates to the new
 * natural height when it flips false. Works for plain block wrappers and for
 * flex items (`flex` is pinned to `0 0 auto` alongside the height so a
 * `flex-1` wrapper stops tracking available space while frozen).
 *
 * Direct style mutation is intentional: this is pure animation plumbing on a
 * wrapper the component otherwise never styles inline, and going through
 * state would re-render the whole (potentially large) list per frame.
 */
export function useSettleHeightAnimation(
  ref: RefObject<HTMLElement | null>,
  pending: boolean,
) {
  const frozenHeightRef = useRef<number | null>(null);
  const cleanupTimerRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clearTimer = () => {
      if (cleanupTimerRef.current !== null) {
        window.clearTimeout(cleanupTimerRef.current);
        cleanupTimerRef.current = null;
      }
    };
    const release = () => {
      clearTimer();
      el.style.transition = "";
      el.style.height = "";
      el.style.flex = "";
      el.style.overflow = "";
    };

    if (pending) {
      // Already frozen (successive keystrokes keep pending true) — hold.
      if (frozenHeightRef.current !== null) return;
      clearTimer();
      const h = el.offsetHeight;
      frozenHeightRef.current = h;
      el.style.transition = "";
      el.style.height = `${h}px`;
      el.style.flex = "0 0 auto";
      // Clip: while frozen (and while animating below) the inner list's
      // natural height can exceed the pinned wrapper height.
      el.style.overflow = "hidden";
      return;
    }

    if (frozenHeightRef.current === null) return;
    const from = frozenHeightRef.current;
    frozenHeightRef.current = null;

    // Measure the natural (settled) height, then animate from → to.
    el.style.transition = "";
    el.style.height = "";
    el.style.flex = "";
    const target = el.offsetHeight;
    if (target === from) {
      release();
      return;
    }
    el.style.height = `${from}px`;
    el.style.flex = "0 0 auto";
    void el.offsetHeight; // commit the start height before transitioning
    el.style.transition = `height ${SETTLE_TRANSITION_MS}ms ease`;
    el.style.height = `${target}px`;
    // Timeout instead of `transitionend`: the event never fires when the tab
    // is backgrounded or the panel unmounts mid-transition.
    cleanupTimerRef.current = window.setTimeout(
      release,
      SETTLE_TRANSITION_MS + 60,
    );
  }, [pending, ref]);
}
